// web-frontend/src/shared/utils/hkPlaces.js
// Hong Kong administrative areas — 18 districts grouped by region (HK Island /
// Kowloon / New Territories) with center coordinates for distance-from-HKCEC
// calculations. Mirrors the shape of carbonService.DISTRICT_COORDINATES so it
// can be plugged into the same code paths.

// =============================================================================
// 18 districts with center coordinates (rough centroids)
// =============================================================================

export const HK_DISTRICT_COORDINATES = {
  // ---- Hong Kong Island ----
  'Hong Kong Island': {
    center: [22.2849, 114.1581],
    districts: {
      'Central and Western': [22.2853, 114.1577],
      'Wan Chai': [22.2783, 114.1747],
      Eastern: [22.2783, 114.2206],
      Southern: [22.2469, 114.1648],
    },
  },
  // ---- Kowloon ----
  Kowloon: {
    center: [22.3193, 114.1694],
    districts: {
      'Yau Tsim Mong': [22.3143, 114.1722],
      'Sham Shui Po': [22.3306, 114.1622],
      'Kowloon City': [22.3287, 114.1903],
      'Wong Tai Sin': [22.3416, 114.1953],
      'Kwun Tong': [22.3128, 114.2256],
    },
  },
  // ---- New Territories ----
  'New Territories': {
    center: [22.3964, 114.1095],
    districts: {
      'Kwai Tsing': [22.3578, 114.1311],
      'Tsuen Wan': [22.3735, 114.1144],
      'Tuen Mun': [22.3911, 113.9745],
      'Yuen Long': [22.4445, 114.0345],
      North: [22.4946, 114.1389],
      'Tai Po': [22.4513, 114.1644],
      'Sha Tin': [22.3815, 114.1898],
      'Sai Kung': [22.3819, 114.27],
      Islands: [22.2616, 113.9461],
    },
  },
};

// =============================================================================
// Localized labels for the 18 districts (en is canonical English name).
// Used by the survey UI when user picks the home country.
// =============================================================================

export const HK_REGION_TRANSLATIONS = {
  'Hong Kong Island': {
    en: 'Hong Kong Island',
    ko: '홍콩섬',
    ja: '香港島',
    zh: '香港島',
  },
  Kowloon: {
    en: 'Kowloon',
    ko: '구룡',
    ja: '九龍',
    zh: '九龍',
  },
  'New Territories': {
    en: 'New Territories',
    ko: '신계',
    ja: '新界',
    zh: '新界',
  },
};

export const HK_DISTRICT_TRANSLATIONS = {
  'Central and Western': {
    en: 'Central and Western', ko: '중서구', ja: '中西区', zh: '中西區',
  },
  'Wan Chai': { en: 'Wan Chai', ko: '완차이', ja: '湾仔', zh: '灣仔' },
  Eastern: { en: 'Eastern', ko: '동구', ja: '東区', zh: '東區' },
  Southern: { en: 'Southern', ko: '남구', ja: '南区', zh: '南區' },
  'Yau Tsim Mong': {
    en: 'Yau Tsim Mong', ko: '유침몽', ja: '油尖旺', zh: '油尖旺',
  },
  'Sham Shui Po': {
    en: 'Sham Shui Po', ko: '심수보', ja: '深水埗', zh: '深水埗',
  },
  'Kowloon City': {
    en: 'Kowloon City', ko: '구룡성', ja: '九龍城', zh: '九龍城',
  },
  'Wong Tai Sin': {
    en: 'Wong Tai Sin', ko: '황대선', ja: '黄大仙', zh: '黃大仙',
  },
  'Kwun Tong': { en: 'Kwun Tong', ko: '관당', ja: '観塘', zh: '觀塘' },
  'Kwai Tsing': { en: 'Kwai Tsing', ko: '규청', ja: '葵青', zh: '葵青' },
  'Tsuen Wan': { en: 'Tsuen Wan', ko: '취안', ja: '荃湾', zh: '荃灣' },
  'Tuen Mun': { en: 'Tuen Mun', ko: '둔문', ja: '屯門', zh: '屯門' },
  'Yuen Long': { en: 'Yuen Long', ko: '원랑', ja: '元朗', zh: '元朗' },
  North: { en: 'North', ko: '북구', ja: '北区', zh: '北區' },
  'Tai Po': { en: 'Tai Po', ko: '대포', ja: '大埔', zh: '大埔' },
  'Sha Tin': { en: 'Sha Tin', ko: '사전', ja: '沙田', zh: '沙田' },
  'Sai Kung': { en: 'Sai Kung', ko: '서공', ja: '西貢', zh: '西貢' },
  Islands: { en: 'Islands', ko: '도서구', ja: '離島', zh: '離島' },
};

/**
 * Translate a HK region (HK Island / Kowloon / New Territories) name.
 * @param {string} regionEn  canonical English name
 * @param {string} lang      'ko' | 'en' | 'ja' | 'zh'
 */
export function translateHkRegion(regionEn, lang) {
  if (!regionEn) return regionEn;
  const entry = HK_REGION_TRANSLATIONS[regionEn];
  return (entry && entry[lang]) || regionEn;
}

/**
 * Translate a HK district name.
 * @param {string} districtEn  canonical English name
 * @param {string} lang        'ko' | 'en' | 'ja' | 'zh'
 */
export function translateHkDistrict(districtEn, lang) {
  if (!districtEn) return districtEn;
  const entry = HK_DISTRICT_TRANSLATIONS[districtEn];
  return (entry && entry[lang]) || districtEn;
}

export function getHkRegions() {
  return Object.keys(HK_DISTRICT_COORDINATES);
}

export function getHkDistricts(region) {
  if (!region || !HK_DISTRICT_COORDINATES[region]) return [];
  return Object.keys(HK_DISTRICT_COORDINATES[region].districts).sort();
}
